/**
 * avatars/avatar-builder/builder-head.js
 * Xây dựng đầu, tóc và textures khuôn mặt (Head & Face) cho BlockyAvatar
 */

import * as THREE from 'three';
import { generateProceduralTexture } from '../avatar-presets.js';

export function buildHead(avatar, torsoGroup, dimensions, materials) {
  const { torsoH, headSize, bodyType } = dimensions;
  const { skinMat } = materials;

  const headGroup = new THREE.Group();
  headGroup.position.set(0, torsoH, 0);
  torsoGroup.add(headGroup);
  avatar.parts.head = headGroup;

  if (bodyType === 'elder') {
    headGroup.rotation.x = -0.08;
  }

  const skinColor = avatar.config.colors.skin;
  const hairColor = avatar.config.colors.hair;
  const hairStyle = avatar.config.features.hairStyle;

  // Mặt trước (+Z)
  const speciesIdForFace = avatar.config.speciesId || avatar.config.species || avatar.config.features?.speciesId || '';
  const faceCanvas = generateProceduralTexture('face', {
    skinColor,
    eyeColor: avatar.config.colors.eyes,
    hairColor,
    hairStyle,
    hasBlush: avatar.config.features.hasBlush,
    eyeType: avatar.config.features.eyeType,
    speciesId: speciesIdForFace,
    hasGlasses: false,
    expression: 'happy',
    ...avatar.getFaceProfile()
  });
  avatar.faceCanvas = faceCanvas;
  const faceTex = new THREE.CanvasTexture(faceCanvas);
  faceTex.colorSpace = THREE.SRGBColorSpace;
  avatar.faceTex = faceTex;
  const faceFrontMat = new THREE.MeshStandardMaterial({ map: faceTex, roughness: 0.7 });

  // Mặt bên phải (+X)
  const headSideRightCanvas = generateProceduralTexture('head_side', {
    skinColor,
    hairColor,
    hairStyle,
    side: 'right'
  });
  avatar.headSideRightCanvas = headSideRightCanvas;
  const headSideRightTex = new THREE.CanvasTexture(headSideRightCanvas);
  headSideRightTex.colorSpace = THREE.SRGBColorSpace;
  avatar.headSideRightTex = headSideRightTex;
  const headSideRightMat = new THREE.MeshStandardMaterial({ map: headSideRightTex, roughness: 0.7 });

  // Mặt bên trái (-X)
  const headSideLeftCanvas = generateProceduralTexture('head_side', {
    skinColor,
    hairColor,
    hairStyle,
    side: 'left'
  });
  avatar.headSideLeftCanvas = headSideLeftCanvas;
  const headSideLeftTex = new THREE.CanvasTexture(headSideLeftCanvas);
  headSideLeftTex.colorSpace = THREE.SRGBColorSpace;
  avatar.headSideLeftTex = headSideLeftTex;
  const headSideLeftMat = new THREE.MeshStandardMaterial({ map: headSideLeftTex, roughness: 0.7 });

  // Mặt đỉnh đầu (+Y)
  const headTopMat = new THREE.MeshStandardMaterial({
    color: hairStyle === 'bald' ? skinColor : hairColor,
    roughness: 0.85
  });
  avatar.headTopMat = headTopMat;

  // Mặt đáy đầu / Cổ (-Y)
  const headBottomMat = skinMat;

  // Mặt sau gáy (-Z)
  const headBackCanvas = generateProceduralTexture('head_back', {
    skinColor,
    hairColor,
    hairStyle
  });
  avatar.headBackCanvas = headBackCanvas;
  const headBackTex = new THREE.CanvasTexture(headBackCanvas);
  headBackTex.colorSpace = THREE.SRGBColorSpace;
  avatar.headBackTex = headBackTex;
  const headBackMat = new THREE.MeshStandardMaterial({ map: headBackTex, roughness: 0.7 });

  avatar.disposables.push(
    faceTex,
    faceFrontMat,
    headSideRightTex,
    headSideRightMat,
    headSideLeftTex,
    headSideLeftMat,
    headTopMat,
    headBackTex,
    headBackMat
  );

  // Mảng 6 vật liệu cho BoxGeometry: [+X, -X, +Y, -Y, +Z, -Z]
  const headMaterials = [
    headSideRightMat, // 0: +X (phải)
    headSideLeftMat, // 1: -X (trái)
    headTopMat, // 2: +Y (đỉnh)
    headBottomMat, // 3: -Y (đáy)
    faceFrontMat, // 4: +Z (trước)
    headBackMat // 5: -Z (gáy)
  ];
  avatar.headMaterials = headMaterials;

  const headGeom = new THREE.BoxGeometry(headSize, headSize, headSize);
  avatar.disposables.push(headGeom);
  const headMesh = new THREE.Mesh(headGeom, headMaterials);
  headMesh.position.y = headSize / 2;
  headMesh.castShadow = false;
  headMesh.receiveShadow = true;
  headGroup.add(headMesh);
  avatar.headMesh = headMesh;

  return headGroup;
}
